import React, { useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import "../style/userProfile/userProfile.css";
import { Link, useHistory } from "react-router-dom";
import axios from "../config/axios";
import { useTranslation } from 'react-i18next';

function MyProfile() {
    const { t } = useTranslation();
    const history = useHistory();
    const [activeTab, setActiveTab] = useState("home");
    const [profileImage, setProfileImage] = useState(null);
    const [userBio, setUserBio] = useState('');
    const [userName, setUserName] = useState('User');
    const [googleProfileLink, setGoogleProfileLink] = useState(null);
    const [facebookProfileLink, setFacebookProfileLink] = useState(null);
    const [twitterProfileLink, setTwitterProfileLink] = useState(null);
    const [showLinkedInLink, setShowLinkedInLink] = useState(false);
    const [linkedInProfileLink, setLinkedInProfileLink] = useState(null);
    const [threadsProfileLink, setThreadsProfileLink] = useState(null);
    const [showGoogleLink, setShowGoogleLink] = useState(false);
    const [showFacebookLink, setShowFacebookLink] = useState(false);
    const [showTwitterLink, setShowTwitterLink] = useState(false);
    const [showThreadsLink, setShowThreadsLink] = useState(false);
    const [following, setFollowing] = useState([]);
    const [registeredEvents, setRegisteredEvents] = useState([]);
    const [loadingFollowing, setLoadingFollowing] = useState(true);
    const [loadingEvents, setLoadingEvents] = useState(true);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));

        // Set initial name from localStorage
        if (user) {
            setUserName(`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User');
        }

        // Check localStorage first for profile image
        const savedImage = localStorage.getItem('profileImage');
        if (savedImage) {
            setProfileImage(savedImage);
        }

        // Fetch profile data from API
        if (user?.id) {
            axios.get(`/user/${user.id}/profile`)
            .then(response => {
                const profile = response.data?.profile;
                const userData = response.data?.user;

                // Update profile image if available and not already loaded from localStorage
                if (profile?.photo && !savedImage) {
                    setProfileImage(profile.photo);
                }

                // Update bio
                if (profile?.biography) {
                    setUserBio(profile.biography);
                }

                // Update name from API data (more current than localStorage)
                if (userData) {
                    const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
                    if (fullName) {
                        setUserName(fullName);
                    }
                }
            })
            .catch(() => {
                // fallback to default values
            });
        }
    }, []);

    useEffect(() => {
        const googleLink = localStorage.getItem('googleProfileLink');
        const facebookLink = localStorage.getItem('facebookProfileLink');
        const twitterLink = localStorage.getItem('twitterProfileLink');
        setGoogleProfileLink(googleLink);
        setFacebookProfileLink(facebookLink);
        setTwitterProfileLink(twitterLink);
    }, []);

    useEffect(() => {
        const storedLinkedInLink = localStorage.getItem('showLinkedInLink');
        if (storedLinkedInLink !== null) {
            setShowLinkedInLink(storedLinkedInLink === 'true');
        }
    }, []);

    useEffect(() => {
        const storedLinkedInLink = localStorage.getItem('linkedInProfileLink');
        if (storedLinkedInLink) {
            setLinkedInProfileLink(storedLinkedInLink);
        }
    }, []);

    useEffect(() => {
        const storedThreadsLink = localStorage.getItem('threadsProfileLink');
        if (storedThreadsLink) {
            setThreadsProfileLink(storedThreadsLink);
        }
    }, []);

    useEffect(() => {
        const storedShowGoogleLink = localStorage.getItem('showGoogleLink');
        const storedShowFacebookLink = localStorage.getItem('showFacebookLink');
        const storedShowTwitterLink = localStorage.getItem('showTwitterLink');
        const storedShowThreadsLink = localStorage.getItem('showThreadsLink');
        setShowGoogleLink(storedShowGoogleLink === 'true');
        setShowFacebookLink(storedShowFacebookLink === 'true');
        setShowTwitterLink(storedShowTwitterLink === 'true');
        setShowThreadsLink(storedShowThreadsLink === 'true');
    }, []);

    // Fetch users that the current user is following
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user?.id) {
            axios.get(`/follow/${user.id}/following`)
                .then(response => {
                    setFollowing(response.data || []);
                    setLoadingFollowing(false);
                })
                .catch(err => {
                    console.error('Error fetching following:', err);
                    setLoadingFollowing(false);
                });
        } else {
            setLoadingFollowing(false);
        }
    }, []);

    // Fetch events the user is registered for
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user?.email) {
            axios.get(`/event/registered/${user.email}`)
                .then(response => {
                    setRegisteredEvents(response.data || []);
                    setLoadingEvents(false);
                })
                .catch(err => {
                    console.error('Error fetching registered events:', err);
                    setLoadingEvents(false);
                });
        } else {
            setLoadingEvents(false);
        }
    }, []);

    const openGmailCompose = () => {
        if (googleProfileLink) {
            const email = googleProfileLink.split('to=')[1];
            const mailtoLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`;
            window.open(mailtoLink, '_blank');
        }
    };

    // Save uploaded image to localStorage
    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                localStorage.setItem("profileImage", base64String);
                setProfileImage(base64String);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const renderShortenedLink = (link, platform) => {
        if (!link) return null;
        let displayText;
        switch (platform) {
            case 'Google':
                displayText = t('MyProfilePage.SendEmailGmail');
                break;
            case 'Facebook':
                displayText = t('MyProfilePage.ViewFacebook');
                break;
            case 'Twitter':
                displayText = t('MyProfilePage.ViewTwitter');
                break;
            case 'LinkedIn':
                displayText = t('MyProfilePage.ViewLinkedIn');
                break;
            case 'Threads':
                displayText = t('MyProfilePage.ViewThreads');
                break;
            default:
                displayText = t('MyProfilePage.VisitProfile');
        }
        return (
            <div className="profile-link-block">
                <span className="profile-link-label">{displayText}:</span>
                <a href={link} target="_blank" rel="noopener noreferrer" className="profile-link">
                    {displayText}
                </a>
            </div>
        );
    };

    return (
        <div className="userProfile-container">
            <style>
                {`
                    .userProfile-left-subbar-item {
                        padding: 10px 20px;
                        cursor: pointer;
                        transition: background-color 0.3s ease-in-out;
                    }
                    .userProfile-left-subbar-item:hover {
                        background-color: rgba(0, 0, 0, 0.1);
                        border-radius: 5px;
                    }
                    .button.active {
                        background-color: #007bff;
                        color: white;
                    }
                    .userProfile-right-editProfile {
                        display: inline-block;
                        padding: 12px 18px;
                        color: #007bff;
                        font-weight: bold;
                        cursor: pointer;
                        transition: text-decoration 0.3s ease-in-out;
                        text-decoration: none;
                    }
                    .userProfile-right-editProfile:hover {
                        text-decoration: underline;
                    }
                    .userProfile-right-image-wrapper {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        margin-bottom: 20px;
                    }
                    .userProfile-right-image {
                        width: 150px;
                        height: 150px;
                        border-radius: 50%;
                        object-fit: cover;
                        border: 2px solid #ccc;
                        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                        transition: opacity 0.2s ease;
                    }
                    .userProfile-right-image:hover {
                        opacity: 0.85;
                        cursor: pointer;
                    }
                    .image-upload {
                        margin-top: 12px;
                        font-size: 14px;
                    }
                    .userProfile-right-userInfo {
                        margin-top: 12px;
                        font-weight: 500;
                        text-align: center;
                    }
                    .profile-links-container {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        margin-top: 20px;
                    }
                    .profile-link-block {
                        display: flex;
                        align-items: center;
                        margin-bottom: 10px;
                    }
                    .profile-link-label {
                        font-weight: bold;
                        margin-right: 5px;
                    }
                    .profile-link {
                        color: #007bff;
                        text-decoration: none;
                    }
                    .profile-link:hover {
                        text-decoration: underline;
                    }
                `}
            </style>
            <div className="userProfile-left">
                <Typography variant="h4" className="userProfile-title">
                    {userName}
                </Typography>
                <div className="userProfile-left-subbar">
                    <Typography
                        variant="h6"
                        className={`userProfile-left-subbar-item button ${activeTab === "home" ? "active" : ""}`}
                        onClick={() => setActiveTab("home")}
                    >
                        {t('MyProfilePage.Home')}
                    </Typography>
                    <Typography
                        variant="h6"
                        className={`userProfile-left-subbar-item button ${activeTab === "about" ? "active" : ""}`}
                        onClick={() => setActiveTab("about")}
                    >
                        {t('MyProfilePage.About')}
                    </Typography>
                </div>
                <hr className="userProfile-left-hr" />
                <div className="userProfile-left-content">
                    {activeTab === "home" ? (
                        <div>
                            <Typography variant="body1">
                                {t('MyProfilePage.WelcomeMessage')}
                            </Typography>
                            <div className="profile-links-container">
                                {showGoogleLink && renderShortenedLink(googleProfileLink, 'Google')}
                                {showFacebookLink && renderShortenedLink(facebookProfileLink, 'Facebook')}
                                {showTwitterLink && renderShortenedLink(twitterProfileLink, 'Twitter')}
                                {showLinkedInLink && renderShortenedLink(linkedInProfileLink, 'LinkedIn')}
                                {showThreadsLink && renderShortenedLink(threadsProfileLink, 'Threads')}
                            </div>

                            {/* Following Section */}
                            <div style={{ marginTop: '30px' }}>
                                <Typography variant="h6" style={{ marginBottom: '15px', fontWeight: 'bold' }}>
                                    {t('MyProfilePage.Following')} ({following.length})
                                </Typography>
                                {loadingFollowing ? (
                                    <Typography variant="body2">{t('MyProfilePage.Loading')}</Typography>
                                ) : following.length === 0 ? (
                                    <Typography variant="body2" style={{ color: '#666' }}>
                                        {t('MyProfilePage.NotFollowing')}
                                    </Typography>
                                ) : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                        {following.map((followedUser, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    padding: '10px 15px',
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    cursor: 'pointer',
                                                    transition: 'background-color 0.2s'
                                                }}
                                                onClick={() => history.push(`/profile/${followedUser.followed_id}`)}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8e8e8'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                            >
                                                <div style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#6750A4',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px'
                                                }}>
                                                    {followedUser.firstname?.charAt(0)?.toUpperCase() || '?'}
                                                </div>
                                                <Typography variant="body2">
                                                    {followedUser.firstname} {followedUser.lastname}
                                                </Typography>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Registered Events Section */}
                            <div style={{ marginTop: '30px' }}>
                                <Typography variant="h6" style={{ marginBottom: '15px', fontWeight: 'bold' }}>
                                    {t('MyProfilePage.RegisteredEvents')} ({registeredEvents.length})
                                </Typography>
                                {loadingEvents ? (
                                    <Typography variant="body2">{t('MyProfilePage.Loading')}</Typography>
                                ) : registeredEvents.length === 0 ? (
                                    <Typography variant="body2" style={{ color: '#666' }}>
                                        {t('MyProfilePage.NoEvents')}
                                    </Typography>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {registeredEvents.map((event, index) => (
                                            <div key={index} style={{
                                                padding: '15px',
                                                backgroundColor: '#f9f9f9',
                                                borderRadius: '8px',
                                                border: '1px solid #e0e0e0'
                                            }}>
                                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                                    {event.title}
                                                </Typography>
                                                <Typography variant="body2" style={{ color: '#666', marginTop: '5px' }}>
                                                    {new Date(event.event_timestamp).toLocaleDateString('en-US', {
                                                        weekday: 'long',
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </Typography>
                                                <Typography variant="body2" style={{ color: '#6750A4', marginTop: '5px' }}>
                                                    {event.event_type?.charAt(0).toUpperCase() + event.event_type?.slice(1)} Event
                                                </Typography>
                                                {event.event_link && (
                                                    <Typography variant="body2" style={{ marginTop: '5px' }}>
                                                        <a href={event.event_link} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                                                            {t('MyProfilePage.JoinLink')}
                                                        </a>
                                                    </Typography>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="userProfile-left-bio">
                            {userBio || t('MyProfilePage.DefaultBio', { name: userName })}
                        </div>
                    )}
                </div>
            </div>
            <div className="userProfile-right">
                <div className="userProfile-right-image-wrapper">
                    <label htmlFor="imageUpload">
                        <img
                            src={profileImage || "blogger.jpg"}
                            alt="user"
                            className="userProfile-right-image"
                        />
                    </label>
                    <input
                        id="imageUpload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="image-upload"
                        style={{ display: "none" }}
                    />
                    <Typography variant="h6" className="userProfile-right-userInfo">
                        {userName}
                    </Typography>
                </div>
                <Link className="editProfile-link" to="editprofile">
                    <span className="userProfile-right-editProfile">{t('MyProfilePage.EditProfile')}</span>
                </Link>
            </div>
        </div>
    );
}

export default MyProfile;
